<template>
  <div class="form-item-file-cell">
    <template v-if="entries.length">
      <div class="form-item-file-cell-files">
        <template v-for="entry in entries">
          <!-- 画像: サムネ。クリックで別タブ表示 -->
          <a
            v-if="entry.isImage && entry.url"
            :key="`img-${entry.path}`"
            class="file-thumb-link"
            :href="entry.url"
            target="_blank"
            rel="noreferrer"
            :title="entry.displayName"
          >
            <img :src="entry.url" :alt="entry.displayName" class="file-thumb" />
          </a>
          <!-- ファイル（または未解決画像）: クリップ＋ファイル名。URL 解決済みなら別タブリンク -->
          <a
            v-else-if="entry.url"
            :key="`file-${entry.path}`"
            class="file-badge"
            :href="entry.url"
            target="_blank"
            rel="noreferrer"
            :title="entry.displayName"
          >
            <feather-icon
              icon="PaperclipIcon"
              svgClasses="h-4 w-4"
              class="file-badge-icon"
            />
            <span class="file-badge-name">{{ entry.displayName }}</span>
          </a>
          <!-- URL 未解決（取得中／失敗）: リンクなしのファイル名表示 -->
          <span
            v-else
            :key="`plain-${entry.path}`"
            class="file-badge file-badge--plain"
            :title="entry.displayName"
          >
            <feather-icon
              icon="PaperclipIcon"
              svgClasses="h-4 w-4"
              class="file-badge-icon"
            />
            <span class="file-badge-name">{{ entry.displayName }}</span>
          </span>
        </template>
      </div>
    </template>
    <span v-else class="aisea-text-14-400-dark-gray">-</span>
  </div>
</template>

<script>
import isString from "lodash/isString";
import {
  createStorageFilePath,
  getDownloadUrlFromStoragePath,
  getDisplayFileName,
  isImageFileByName,
} from "@/utils/operations";

// テーブルセル内の value_files（ファイル）を read-only で表示するコンポーネント。
export default {
  name: "FormItemFileCell",
  props: {
    // value_files 配列（要素は {name, content_type, token, uploaded_at} もしくは文字列）
    valueFiles: {
      type: Array,
      default: () => [],
    },
    // ストレージパス組立に使う認証ユーザーの企業ID（親で AuthService から解決して渡す）
    enterpriseId: {
      type: [String, Number],
      default: "",
    },
    reportId: {
      type: [String, Number],
      default: "",
    },
    // テンプレ列（マスタ側）の form_version_item_id
    formVersionItemId: {
      type: [String, Number],
      default: "",
    },
    // 可変行テーブルの行番号（0 始まり）。指定時は rows:{repeatIndex}/ を含むパスを組み立てる
    repeatIndex: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      urlByPath: {}, // { [storagePath]: downloadUrl } 解決済みのダウンロードURL
    };
  },
  computed: {
    hasEnterpriseId() {
      return String(this.enterpriseId).length > 0;
    },
    // 表示対象ファイル（name を持つもの）。文字列要素は {name} に正規化
    files() {
      if (!Array.isArray(this.valueFiles)) return [];
      return this.valueFiles
        .filter(f => f && (isString(f) || isString(f.name)))
        .map(f => (isString(f) ? { name: f } : f));
    },
    // 各ファイルのストレージパス（URL 解決のトリガー。urlByPath には依存させない）
    filePaths() {
      const {
        files,
        enterpriseId,
        reportId,
        formVersionItemId,
        repeatIndex,
      } = this;
      return files.map(file => ({
        name: file.name,
        path: createStorageFilePath(
          String(enterpriseId),
          String(reportId),
          String(formVersionItemId),
          file.name,
          repeatIndex
        ),
      }));
    },
    // テンプレート描画用エントリ（表示名・画像判定・解決済みURL）
    entries() {
      return this.filePaths.map(({ name, path }) => ({
        path,
        name,
        displayName: getDisplayFileName(name),
        isImage: isImageFileByName(name),
        url: this.urlByPath[path] || "",
      }));
    },
  },
  watch: {
    filePaths: {
      async handler() {
        if (!this.hasEnterpriseId) return;
        for (const { path } of this.filePaths) {
          if (this.urlByPath[path]) continue;
          try {
            const url = await getDownloadUrlFromStoragePath(path);
            this.$set(this.urlByPath, path, url);
          } catch {
            // 取得失敗時はリンクなしのファイル名表示にフォールバック
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.form-item-file-cell {
  .form-item-file-cell-files {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .file-thumb-link {
    display: inline-block;
    line-height: 0;
  }

  .file-thumb {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid $aisea-light-gray;
  }

  .file-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    padding: 4px 8px;
    border-radius: 4px;
    background-color: #f5f7fa;
    color: #1b5e83;
    text-decoration: none;
    cursor: pointer;

    &--plain {
      color: inherit;
      cursor: default;
    }
  }

  .file-badge-icon {
    flex: 0 0 auto;
  }

  .file-badge-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
  }
}
</style>
